import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackEvent, setAnalyticsUser } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Global axios config
// withCredentials sends cookies cross-origin (requires SameSite=none on server).
// We ALSO set Authorization header as a reliable fallback for cross-origin
// scenarios where cookies may be blocked (e.g. Safari ITP, iOS WebView).
// ─────────────────────────────────────────────────────────────────────────────
axios.defaults.withCredentials = true;
// CSRF defense: a custom header the backend requires on cookie-authenticated,
// state-changing requests. Cross-site attackers can't set this without a CORS
// preflight (which our origin allowlist rejects).
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const TOKEN_KEY = 'mk_session'; // Session token key (sessionStorage — not localStorage)
const OAUTH_RETURN_KEY = 'mk_oauth_return'; // where to land after a "connect" redirect
const PKCE_VERIFIER_KEY = 'mk_pkce_verifier'; // PKCE code_verifier (per provider)
const OAUTH_STATE_KEY = 'mk_oauth_state';     // CSRF state (per provider)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Login (sign-in) requests ONLY non-sensitive scopes — no "unverified app"
// warning. Gmail/Calendar are sensitive scopes requested later, on demand
// (incremental authorization), only when the user connects those features.
const GOOGLE_LOGIN_SCOPES = ['openid', 'email', 'profile'].join(' ');
// Gmail (gmail.send) is a RESTRICTED scope requiring an annual CASA assessment.
// Gate it behind REACT_APP_ENABLE_GMAIL so we can ship Calendar-only (Calendar
// is only "sensitive") and defer CASA. Flip the env var to 'true' to re-enable.
export const GMAIL_ENABLED = process.env.REACT_APP_ENABLE_GMAIL === 'true';
// Calendar is handled via the no-OAuth ICS subscription feed, so we no longer
// request calendar.events. The only optional Google service scope is gmail.send
// (gated behind CASA); when disabled, Google is login-only (no verification).
const GOOGLE_SERVICE_SCOPES = (GMAIL_ENABLED
  ? ['https://www.googleapis.com/auth/gmail.send']
  : []).join(' ');

// ─── PKCE (S256) + state helpers — hardens the OAuth code flow against
// authorization-code interception / impersonation (Google security rec). ───────
function _randomString(len = 64) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').slice(0, len);
}

async function _codeChallenge(verifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function _pkceParams(provider) {
  const verifier = _randomString(64);
  const state = _randomString(32);
  try {
    sessionStorage.setItem(`${PKCE_VERIFIER_KEY}_${provider}`, verifier);
    sessionStorage.setItem(`${OAUTH_STATE_KEY}_${provider}`, state);
  } catch { /* ignore */ }
  const challenge = await _codeChallenge(verifier);
  return { challenge, state };
}

// Read + clear the stored verifier/state after the redirect returns.
export function consumePkce(provider) {
  const vk = `${PKCE_VERIFIER_KEY}_${provider}`;
  const sk = `${OAUTH_STATE_KEY}_${provider}`;
  let verifier = null, state = null;
  try {
    verifier = sessionStorage.getItem(vk);
    state = sessionStorage.getItem(sk);
    sessionStorage.removeItem(vk);
    sessionStorage.removeItem(sk);
  } catch { /* ignore */ }
  return { verifier, state };
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export async function buildGoogleAuthUrl(mode = 'login') {
  const redirectUri = `${window.location.origin}/auth/google`;
  const connect = mode === 'connect';
  const scope = connect ? `${GOOGLE_LOGIN_SCOPES} ${GOOGLE_SERVICE_SCOPES}` : GOOGLE_LOGIN_SCOPES;
  const { challenge, state } = await _pkceParams('google');
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  // Offline access, forced re-consent, and incremental scope-merging are ONLY
  // meaningful for the "connect a service" flow (which needs a refresh token and
  // must merge with already-granted scopes). Sending include_granted_scopes on a
  // plain LOGIN silently re-adds any sensitive scopes the user granted in the past
  // (gmail/calendar), which keeps Google's "unverified/sensitive" warning up even
  // though login only requests openid/email/profile. Keep login minimal.
  if (connect) {
    params.set('access_type', 'offline');
    params.set('prompt', 'consent');
    params.set('include_granted_scopes', 'true');
  }
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const MICROSOFT_TENANT = process.env.REACT_APP_MICROSOFT_TENANT || 'common';
const MICROSOFT_LOGIN_SCOPES = ['openid', 'email', 'profile', 'offline_access', 'User.Read'].join(' ');
// Calendar via ICS feed (no scope). Only Mail.Send remains as an optional service.
const MICROSOFT_SERVICE_SCOPES = ['https://graph.microsoft.com/Mail.Send'].join(' ');

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export async function buildMicrosoftAuthUrl(mode = 'login') {
  const redirectUri = `${window.location.origin}/auth/microsoft`;
  const connect = mode === 'connect';
  const scope = connect ? `${MICROSOFT_LOGIN_SCOPES} ${MICROSOFT_SERVICE_SCOPES}` : MICROSOFT_LOGIN_SCOPES;
  const { challenge, state } = await _pkceParams('microsoft');
  const params = new URLSearchParams({
    client_id: MICROSOFT_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    response_mode: 'query',
    scope,
    prompt: 'consent',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  return `https://login.microsoftonline.com/${MICROSOFT_TENANT}/oauth2/v2.0/authorize?${params.toString()}`;
}

function saveToken(token) {
  if (!token) return;
  sessionStorage.setItem(TOKEN_KEY, token);
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  delete axios.defaults.headers.common['Authorization'];
}

function loadToken() {
  const t = sessionStorage.getItem(TOKEN_KEY);
  if (t) axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  return t;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore token from sessionStorage so Authorization header is set
    // before the /auth/me call — needed for cross-origin requests where
    // cookies (SameSite=none) may take a moment to propagate.
    const token = loadToken();

    // On public pages with no token, skip the /auth/me probe to avoid a noisy
    // 401 (landing, /a/:token, auth pages). On any other route (e.g. /capture
    // opened in a NEW window/tab, which has no per-tab sessionStorage token),
    // still probe /auth/me so the session restores via the httpOnly cookie.
    const path = window.location.pathname;
    const isPublicPath = ['/', '/landing', '/login', '/register', '/privacy', '/terms'].includes(path)
      || path.startsWith('/a/');
    if (!token && isPublicPath) {
      setUser(null);
      setLoading(false);
      return;
    }

    axios.get(`${API}/auth/me`)
      .then(res => setUser(res.data))
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = res.data;
    saveToken(token); // Sets Authorization header + sessionStorage
    setUser(userData);
    setAnalyticsUser(userData.id, { home_region: userData.home_region, vertical: userData.vertical });
    trackEvent('login', { method: 'password' });
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token, user: userData } = res.data;
    saveToken(token);
    setUser(userData);
    setAnalyticsUser(userData.id);
    trackEvent('sign_up', { method: 'password' });
    return userData;
  };

  // Start Google OAuth (redirects the whole window to Google's consent screen)
  const loginWithGoogle = async () => {
    window.location.href = await buildGoogleAuthUrl('login');
  };

  // Incremental authorization: request Gmail + Calendar scopes on demand.
  // Returns the user to `returnTo` (default /settings) after consent.
  const connectGoogle = async (returnTo = '/settings') => {
    const dest = typeof returnTo === 'string' ? returnTo : '/settings';
    try { sessionStorage.setItem(OAUTH_RETURN_KEY, dest); } catch { /* ignore */ }
    window.location.href = await buildGoogleAuthUrl('connect');
  };

  // Exchange the authorization code returned by Google for an app session
  const completeGoogleLogin = async (code, returnedState) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = `${window.location.origin}/auth/google`;
    const { verifier, state } = consumePkce('google');
    if (state && returnedState && state !== returnedState) throw new Error('Invalid OAuth state — please try signing in again.');
    const res = await axios.post(`${API}/auth/google`, { code, redirect_uri: redirectUri, code_verifier: verifier });
    const { token, user: userData } = res.data;
    saveToken(token);
    setUser(userData);
    setAnalyticsUser(userData.id, { home_region: userData.home_region, vertical: userData.vertical });
    trackEvent('login', { method: 'google' });
    return userData;
  };

  // Start Microsoft OAuth (redirects the whole window to Microsoft's consent screen)
  const loginWithMicrosoft = async () => {
    window.location.href = await buildMicrosoftAuthUrl('login');
  };

  const connectMicrosoft = async (returnTo = '/settings') => {
    const dest = typeof returnTo === 'string' ? returnTo : '/settings';
    try { sessionStorage.setItem(OAUTH_RETURN_KEY, dest); } catch { /* ignore */ }
    window.location.href = await buildMicrosoftAuthUrl('connect');
  };

  // Exchange the authorization code returned by Microsoft for an app session
  const completeMicrosoftLogin = async (code, returnedState) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = `${window.location.origin}/auth/microsoft`;
    const { verifier, state } = consumePkce('microsoft');
    if (state && returnedState && state !== returnedState) throw new Error('Invalid OAuth state — please try signing in again.');
    const res = await axios.post(`${API}/auth/microsoft`, { code, redirect_uri: redirectUri, code_verifier: verifier });
    const { token, user: userData } = res.data;
    saveToken(token);
    setUser(userData);
    setAnalyticsUser(userData.id, { home_region: userData.home_region, vertical: userData.vertical });
    trackEvent('login', { method: 'microsoft' });
    return userData;
  };

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    clearToken();
    setUser(null);
  }, []);

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, connectGoogle, completeGoogleLogin, loginWithMicrosoft, connectMicrosoft, completeMicrosoftLogin, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
